import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import { colors, reactionsMap } from '@playbookux/playbook-web-core';

import { GET_TAGS_BY_COMPANY_ID } from '../../graphql/tags';

const useTags = (currentCompany) => {
  const {
    UNGROUPED_TAG_COLOR,
    REACTIONS_TAG_COLOR,
  } = colors;
  const {
    loading,
    error,
    data = {},
    refetch,
  } = useQuery(GET_TAGS_BY_COMPANY_ID, {
    variables: {
      companyId: currentCompany,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    refetchOnWindowFocus: true,
  });

  const { tags = [], tagGroups = [] } = data;

  const groupedTags = useMemo(() => {
    const tagsWoReactions = tags.filter((tag) => tag.tagType !== 'reaction');
    const tagsGrouped = (_.groupBy(tagsWoReactions, (tag) => tag.tagGroup && tag.tagGroup.id));

    const memoTag = _.map(tagGroups, (tagGroup) => {
      const groupTags = _.chain(tagsGrouped)
        .get(tagGroup.id, [])
        .orderBy((x) => x.name.toLowerCase())
        .value();
      const countTagHighlight = _.sumBy(groupTags, 'count');

      return ({
        ...tagGroup,
        key: tagGroup.id,
        countTagHighlight,
        tags: groupTags,
      });
    });

    const ungroupedTags = _.chain(tagsGrouped)
      .get('null', [])
      .orderBy((x) => x.name.toLowerCase())
      .value();

    const UNGROUPED = {
      id: null,
      countTagHighlight: _.sumBy(ungroupedTags, 'count'),
      key: 'ungrouped',
      name: 'Ungrouped',
      description: 'Tags that don\'t belong to a group',
      color: UNGROUPED_TAG_COLOR,
      tags: ungroupedTags,
    };

    memoTag.push(UNGROUPED);

    return memoTag || [];
  }, [tagGroups, tags]);

  const reactions = useMemo(() => tags
    .filter((tag) => tag.tagType === 'reaction')
    .map((reaction) => ({
      id: reaction.id,
      count: reaction.count,
      key: reaction.name,
      name: _.capitalize(reaction.name),
      icon: reactionsMap[reaction.name].icon,
      backgroundColor: reactionsMap[reaction.name].backgroundColor,
      color: reactionsMap[reaction.name].color,
      order: reactionsMap[reaction.name].order,
    })).sort((a, b) => a.order - b.order), [tags]);

  const groupedReactions = useMemo(() => ({
    id: 'reaction',
    countTagHighlight: _.sumBy(reactions, 'count'),
    key: 'reactions',
    name: 'Reactions',
    description: '',
    color: REACTIONS_TAG_COLOR,
    tags: reactions,
  }), [reactions]);

  const groupedAll = useMemo(() => (_.concat(groupedTags, groupedReactions)), [groupedReactions, groupedTags]);

  return {
    reactions,
    groupedTags,
    groupedReactions,
    groupedAll,
    refetch,
    error,
    loading,
  };
};

export default useTags;
